<script lang="ts">
  import { apiClient } from '@/utils/api';
  import PublicProfileView from '@/components/public-profile/PublicProfile.svelte';
  import { buildPublicLinkList, toViewProfile } from '@/lib/public-profile-view';

  interface Profile {
    display_name: string;
    username: string;
    /** 서버 합성 문자열(소속 / 부서 / 팀) — `public_profiles_controller#profile_json`. */
    department?: string;
    bio?: string;
    avatar_url?: string;
    avatar_initial?: string;
    social_links?: Record<string, string>;
    stats?: {
      posts_count?: number;
      followers_count?: number;
      presentations_count?: number;
    };
    is_verified?: boolean;
    role?: string;
    seo_title?: string;
    seo_description?: string;
  }

  interface LinkItem {
    id: string;
    title: string;
    description?: string;
    url: string;
    click_redirect_url?: string;
    emoji?: string;
    thumbnail_url?: string;
    link_type: 'post' | 'external' | 'file';
    visibility?: 'public' | 'internal';
    clicks_count?: number;
    link_group_id?: string | null;
  }

  interface LinkGroup {
    id: string;
    title: string;
    emoji?: string;
    is_collapsed?: boolean;
    visibility?: string;
    links?: LinkItem[];
  }

  interface PresentationItem {
    id: string;
    slug: string;
    title: string;
    description?: string;
    thumbnail_url?: string;
    category?: string;
    presented_at?: string;
    is_published?: boolean;
    show_on_profile?: boolean;
  }

  interface Theme {
    template?:
      | 'clean'
      | 'dark'
      | 'glass'
      | 'aurora'
      | 'corporate'
      | 'bento'
      | 'minimal'
      | 'ocean'
      | 'neubrutalism'
      | 'nature';
    button_variant?: string;
    button_radius_preset?: string;
    button_shadow?: string;
    bg_color?: string;
    text_color?: string;
    title_color?: string;
    accent_color?: string;
    bg_image_url?: string;
    wallpaper_style?: string;
    wallpaper_pattern?: string;
    font_heading?: string;
    font_body?: string;
    button_color?: string;
    button_text_color?: string;
    bg_gradient?: string;
    card_bg_color?: string;
    card_opacity?: number;
    spacing?: string;
    hide_branding?: boolean;
    social_icon_style?: string;
    theme_preset_id?: string;
  }

  let {
    profile,
    links = [],
    groups = [],
    presentations = [],
    total_presentations_count = 0,
    theme: savedTheme = { template: 'clean' },
    short_url = null,
    email_capture_enabled = false,
    email_capture_title = '소식 받기',
    meta = undefined,
    is_internal_viewer = false,
    is_owner = false,
    live_preview = false,
  }: {
    profile: Profile;
    links: LinkItem[];
    groups?: LinkGroup[];
    presentations?: PresentationItem[];
    total_presentations_count?: number;
    theme: Theme;
    short_url?: string | null;
    email_capture_enabled?: boolean;
    email_capture_title?: string;
    meta?: { title?: string; description?: string };
    is_internal_viewer?: boolean;
    is_owner?: boolean;
    /** 편집기 iframe 안에서만 true — 서버 `public_profiles_controller#show`. */
    live_preview?: boolean;
  } = $props();

  // 편집기 미리보기 채널. 편집기(`PhonePreview.svelte`)가 저장 전 테마를 postMessage 로
  // 보내면 서버가 내린 테마 대신 그것으로 그린다. 링크·프로필은 그대로 서버 판정을 쓴다 —
  // 이 채널이 덮는 것은 디자인뿐이다.
  // 🔴 같은 출처에서 온 메시지만 받는다. 다른 출처가 이 페이지를 iframe 으로 열어도
  //    테마를 바꿔 끼울 수 없다. 서버 플래그가 꺼져 있으면 리스너 자체를 달지 않는다.
  const THEME_PREVIEW_MESSAGE = 'axhub:theme-preview';
  const THEME_PREVIEW_READY_MESSAGE = 'axhub:theme-preview-ready';

  let themeOverride = $state<Theme | null>(null);
  const theme = $derived(themeOverride ?? savedTheme);

  // 수신 테마의 모양 검사. 값은 결국 `resolveTheme` 을 거쳐 style 속성 문자열에 보간되므로
  // 선언을 닫을 수 있는 문자(`;` `{` `}` `<` `>`)가 든 문자열은 버린다. 배경 이미지 URL 은
  // 거르지 않고 **정규화**한다 — http(s) 만 받고, 따옴표 없는 `url(...)` 에 들어가므로
  // 괄호·따옴표·공백·세미콜론을 퍼센트 인코딩한다(`photo(1).png` 같은 정상 URL 을
  // 버리지 않으면서 sink 를 닫지 못하게, codex P2). 저장 경로의 서버 검증과 별개로
  // 이 채널만의 한 겹이다. 객체 값(custom_css·og_settings)은 렌더러가 읽지 않으므로
  // 통과시키지 않는다.
  const UNSAFE_CSS_VALUE = /[;{}<>\\]|url\s*\(|expression\s*\(|@import/i;
  // 색 필드는 색 문법만, 그래디언트는 *-gradient(...) 하나만 받는다 — "선언을 못 닫게"에
  // 더해 "필드가 뜻하는 형식만" 통과시키는 한 겹(kimi P2, 2026-09-15).
  const COLOR_FIELDS = new Set([
    'bg_color', 'text_color', 'title_color', 'accent_color',
    'button_color', 'button_text_color', 'card_bg_color',
  ]);
  // 색 함수 안은 숫자·단위(deg·turn)·음수·`/`·`none` 까지 허용하되 괄호는 못 연다.
  const COLOR_VALUE = /^(#[0-9a-f]{3,8}|(rgb|rgba|hsl|hsla)\([0-9a-z.,%\s/\-]+\)|[a-z]{3,20})$/i;
  // 최상위 함수가 정확히 하나여야 한다 — 닫는 괄호 뒤에 `, image-set(...)` 같은 꼬리가
  // 붙지 못하게 괄호 중첩을 한 단계(안쪽 rgb() 등)까지만 허용하고 끝을 고정한다(codex P2).
  const GRADIENT_VALUE = /^(linear|radial|conic)-gradient\((?:[^(){};<>]|\([^(){};<>]*\))*\)$/i;

  function normalizeImageUrl(value: string): string | null {
    if (value === '') return '';
    let parsed: URL;
    try {
      parsed = new URL(value, window.location.origin);
    } catch {
      return null;
    }
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
    // encodeURIComponent 는 `(` `)` `'` 를 그대로 둔다(unreserved). 직접 매핑한다.
    return parsed.href.replace(/[()'"\s;]/g, (ch) => URL_ESCAPES[ch] ?? encodeURIComponent(ch));
  }
  const URL_ESCAPES: Record<string, string> = { '(': '%28', ')': '%29', "'": '%27', '"': '%22', ';': '%3B', ' ': '%20' };

  function sanitizeIncomingTheme(input: Record<string, unknown>): Theme {
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(input)) {
      if (value === null || typeof value === 'number' || typeof value === 'boolean') {
        out[key] = value;
        continue;
      }
      if (typeof value !== 'string') continue;
      const text = value.trim();
      if (key === 'bg_image_url') {
        const url = normalizeImageUrl(text);
        if (url !== null) out[key] = url;
        continue;
      }
      if (UNSAFE_CSS_VALUE.test(text)) continue;
      if (text !== '' && COLOR_FIELDS.has(key) && !COLOR_VALUE.test(text)) continue;
      if (text !== '' && key === 'bg_gradient' && !GRADIENT_VALUE.test(text)) continue;
      out[key] = text;
    }
    return out as Theme;
  }

  $effect(() => {
    if (!live_preview || typeof window === 'undefined' || window.parent === window) return;

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      // 보낸 쪽도 좁힌다 — 이 페이지를 품은 부모 창만. 형제 iframe·opener 는 받지 않는다.
      if (event.source !== window.parent) return;
      const data = event.data;
      if (!data || data.type !== THEME_PREVIEW_MESSAGE) return;
      if (!data.theme || typeof data.theme !== 'object') return;
      themeOverride = sanitizeIncomingTheme(data.theme as Record<string, unknown>);
    };

    window.addEventListener('message', onMessage);
    // 새로 뜬(또는 다시 뜬) 문서가 준비됐음을 알린다 — 편집기는 이 신호를 받고
    // 저장 전 변경이 남아 있으면 다시 보낸다.
    window.parent.postMessage({ type: THEME_PREVIEW_READY_MESSAGE }, window.location.origin);
    return () => window.removeEventListener('message', onMessage);
  });

  let subscriberEmail = $state('');
  let subscriberName = $state('');
  let subscribing = $state(false);
  let subscribeMessage = $state('');
  let subscribeError = $state('');
  let copiedShortUrl = $state(false);

  // Track collapsed state per group id. Groups with isCollapsed=true start collapsed.
  // svelte-ignore state_referenced_locally -- intentional: collapsed state initialized from prop
  let collapsedGroups = $state<Record<string, boolean>>(
    Object.fromEntries(
      groups
        .filter((g) => (g as LinkGroup & { is_collapsed?: boolean }).is_collapsed)
        .map((g) => [g.id, true])
    )
  );

  function absoluteUrl(pathOrUrl: string) {
    if (pathOrUrl.startsWith('http://') || pathOrUrl.startsWith('https://')) return pathOrUrl;
    if (typeof window === 'undefined') return pathOrUrl;
    return `${window.location.origin}${pathOrUrl}`;
  }

  async function handleSubscribe() {
    subscribing = true;
    subscribeMessage = '';
    subscribeError = '';

    try {
      const response = await apiClient.post<{ email_capture?: { email: string } }>(
        `/@${profile.username}/subscribe`,
        {
          email: subscriberEmail,
          name: subscriberName || null,
          source: 'profile',
        },
        { skipCaseConversion: true }
      );

      if (response.success) {
        subscribeMessage = '구독이 등록되었습니다.';
        subscriberEmail = '';
        subscriberName = '';
      } else {
        subscribeError = response.error || '구독을 등록하지 못했습니다.';
      }
    } finally {
      subscribing = false;
    }
  }

  async function copyShortUrl() {
    if (!short_url || typeof navigator === 'undefined' || !navigator.clipboard) return;

    await navigator.clipboard.writeText(absoluteUrl(short_url));
    copiedShortUrl = true;
    setTimeout(() => {
      copiedShortUrl = false;
    }, 2000);
  }

  const socialLinks = $derived(
    Object.entries(profile.social_links || {})
      .filter(([, url]) => Boolean(url))
      .map(([platform, url]) => ({ platform, url }))
  );

  const viewProfile = $derived(toViewProfile(profile));

  // Filter a single link: hide internal links for non-internal viewers.
  // 서버(`User#public_profile_links`)가 이미 public 만 보내므로 이중 안전장치다.
  function isLinkVisible(link: LinkItem): boolean {
    if (link.visibility === 'internal' && !is_internal_viewer) return false;
    return true;
  }

  // 배치(그룹 접두·접힘·미분류 뒤붙임)는 편집기 미리보기와 공유한다 — lib/public-profile-view.
  const viewLinks = $derived(
    buildPublicLinkList({
      links: links.filter(isLinkVisible),
      groups: groups.map((group) => ({
        ...group,
        links: group.links ? group.links.filter(isLinkVisible) : undefined,
      })),
      isGroupCollapsed: (group) => Boolean(collapsedGroups[group.id]),
    })
  );
</script>

<svelte:head>
  <title>{meta?.title || `${profile.display_name} (@${profile.username})`}</title>
  {#if meta?.description || profile.seo_description || profile.bio}
    <meta name="description" content={meta?.description || profile.seo_description || profile.bio} />
  {/if}
</svelte:head>

<PublicProfileView
  theme={theme.template || 'clean'}
  themeConfig={theme}
  profile={viewProfile}
  links={viewLinks}
  shortUrl={short_url ? absoluteUrl(short_url) : null}
  copiedShortUrl={copiedShortUrl}
  emailCaptureEnabled={email_capture_enabled}
  emailCaptureTitle={email_capture_title}
  subscribeMessage={subscribeMessage}
  subscribeError={subscribeError}
  subscriberEmail={subscriberEmail}
  subscriberName={subscriberName}
  subscribing={subscribing}
  onCopyShortUrl={copyShortUrl}
  onSubscriberEmailChange={(value: string) => (subscriberEmail = value)}
  onSubscriberNameChange={(value: string) => (subscriberName = value)}
  onSubscribe={handleSubscribe}
  {socialLinks}
  {presentations}
  {total_presentations_count}
  {is_owner}
  compact={false}
  showTopActions={true}
  showStats={profile.stats !== undefined}
/>
